import type { Editor } from '@tiptap/react'
// Deze twee symbolen moeten uit `@tiptap/y-tiptap` komen en niet uit `y-prosemirror`: dat is
// een ander PluginKey-object, en dan mislukt het stil.
import { ySyncPluginKey } from '@tiptap/y-tiptap'
import * as Y from 'yjs'
import { attributionOf } from './attribution.js'

/**
 * Terugkijken met auteurskleuren, en er weer uit komen (ontwerp 6.9).
 *
 * Twee weergaven, en het zijn dezelfde twee als in de CitatenPOC:
 *
 * 1. **Wie schreef wat.** De huidige stand tegen een lege beginstand: alles wat er staat krijgt
 *    de kleur van zijn auteur. Inhoud zonder auteur blijft ongemarkeerd, en dat is precies de
 *    inhoud die de pod bij het openen van de generatie heeft gezaaid (zie `markerOf` in
 *    `ychange.ts`).
 * 2. **Wat is er veranderd sinds deze stand.** Twee snapshots tegen elkaar: toevoegingen
 *    gekleurd, verwijderingen doorgehaald. Dat werkt alleen doordat de opruiming van
 *    verwijderde inhoud uit staat (valkuil 1); met `gc: true` was verwijderde tekst niet meer
 *    te reconstrueren.
 *
 * Een snapshot betekent alleen iets tegen het document waaruit hij komt, dus deze weergaven
 * werken binnen één generatie (6.9). Waar de snapshots vandaan komen is hier met opzet niet
 * bepaald: de nulsnapshot van de generatie staat in de opslag en de versielijst komt met M6
 * (R-137). Deze module is de techniek eronder.
 *
 * **Het verlaten van de geschiedenismodus heeft drie afzonderlijke beveiligingen nodig**
 * (valkuil 8). Ze staan bij {@link leaveHistory} en ze zijn alle drie in de POC met
 * schade opgelost; ze zijn hier overgenomen en niet opnieuw bedacht.
 */

/** Hoe lang na het verlaten de bewerkbare stand wordt herbevestigd. */
export const RECONFIRMATION_MS = 400

/** Een stand van het document om tegen te kijken. */
export type State = Y.Snapshot

/** De stand van nu, om later tegen te vergelijken. */
export function currentState(doc: Y.Doc): State {
  return Y.snapshot(doc)
}

/** Een stand als bytes, voor wie hem wil bewaren of over de lijn sturen. */
export function stateToBytes(snapshot: State): Uint8Array {
  return Y.encodeSnapshot(snapshot)
}

/** Een stand uit bytes. Gooit met een leesbare melding als de bytes stuk zijn. */
export function stateFromBytes(bytes: Uint8Array): State {
  try {
    return Y.decodeSnapshot(bytes)
  } catch (error) {
    // Niet de kryptische fout van de decoder doorlaten: de aanroeper moet hierop naar de
    // levende stand kunnen terugvallen en dat vraagt een fout die zegt wat er mis is.
    throw new Error('deze stand is beschadigd en niet te lezen', { cause: error })
  }
}

/** De editors van één sectie, in de orde waarin ze op het scherm staan. */
export type Editors = Iterable<Editor>

/**
 * Zet de editors in de weergave "wie schreef wat" van deze stand.
 *
 * De editors worden hierdoor vanzelf alleen-lezen: y-tiptap maakt de view read-only zolang er
 * een snapshot in zijn plugin-stand staat. Dat is de bedoeling, en het is ook de bron van de
 * drie beveiligingen bij het verlaten.
 */
export function enterHistory(doc: Y.Doc, editors: Editors, snapshot: State): void {
  attributionOf(doc).refreshColors()
  setState(editors, snapshot, Y.emptySnapshot)
}

/**
 * Zet de editors in de weergave "wat is er veranderd sinds deze stand": deze stand als
 * beginpunt en de levende stand als eindpunt.
 */
export function compareWithNow(doc: Y.Doc, editors: Editors, snapshot: State): void {
  attributionOf(doc).refreshColors()
  setState(editors, Y.snapshot(doc), snapshot)
}

/** Of deze editor nu een stand uit de geschiedenis toont. */
export function inHistory(editor: Editor): boolean {
  if (editor.isDestroyed) return false
  const snapshot = ySyncPluginKey.getState(editor.state) as
    | { snapshot?: unknown; prevSnapshot?: unknown }
    | undefined
  return snapshot !== undefined && (snapshot.snapshot != null || snapshot.prevSnapshot != null)
}

function setState(editors: Editors, snapshot: Y.Snapshot, prevSnapshot: Y.Snapshot): void {
  for (const editor of editors) {
    if (editor.isDestroyed) continue
    const { state, view } = editor
    view.dispatch(
      state.tr
        .setMeta(ySyncPluginKey, { snapshot, prevSnapshot })
        // Niet op de undo-stapel: terugkijken is geen bewerking.
        .setMeta('addToHistory', false),
    )
  }
}

/**
 * Terug naar de levende, bewerkbare stand.
 *
 * Drie beveiligingen, en ze lossen elk iets anders op. Alle drie zijn in de CitatenPOC met
 * schade ontdekt (valkuil 8); wie er één weglaat laat een veld alleen-lezen of gekleurd achter
 * en de invuller denkt dat zijn formulier op slot zit.
 *
 * **Eén. De meta op null zetten is niet genoeg.** y-tiptap rendert alleen opnieuw bij een
 * snapshot die niet null is, dus de gekleurde weergave blijft staan. De juiste uitgang is
 * `binding.unrenderSnapshot()`: die tekent het levende fragment opnieuw, zonder kleuren, en
 * wist de meta in dezelfde transactie.
 *
 * **Twee. De bewerkbaarheid moet expliciet terug.** Tiptap-React synchroniseert bij elke
 * re-render `editable` terug naar wat de editor op dat moment is. In de geschiedenismodus is
 * dat false, dus een willekeurige re-render terwijl je terugkijkt (de opslagindicator die
 * tikt, een medeschrijver die iets typt) bakt `editable: false` vast. Het wissen van de meta
 * herstelt dat niet, want de view rekent `editable && snapshot == null`.
 *
 * **Drie. Er is een race met een late snapshot-render.** Terwijl je terugkijkt kan een
 * wijziging van een medeschrijver een nieuwe render inplannen; vuurt die net ná het verlaten,
 * dan staat de meta weer en blijft het veld alleen-lezen. Daarom wordt de levende stand
 * ongeveer {@link RECONFIRMATION_MS} milliseconden herbevestigd. Er wordt op **beide** signalen
 * gecontroleerd, de meta en de werkelijk gerenderde kleuren, want een race kan die twee
 * ontkoppelen.
 */
export function leaveHistory(editors: Editors): void {
  const list = [...editors]

  const restoreOne = (editor: Editor): void => {
    // Binnen het herbevestigingsvenster kan een editor zijn vernietigd (de sectie sluit, de
    // test ruimt op). Dan geeft `editor.view` een stub waarop `.dom` gooit.
    if (editor.isDestroyed) return
    const snapshot = ySyncPluginKey.getState(editor.state) as
      | { snapshot?: unknown; prevSnapshot?: unknown; binding?: { unrenderSnapshot(): void } }
      | undefined
    const hasMetaState = snapshot !== undefined && (snapshot.snapshot != null || snapshot.prevSnapshot != null)
    const hasColors = editor.view.dom.querySelector('.ef-attributie') !== null

    // Beveiliging twee. Idempotent: doet niets als hij al bewerkbaar is.
    if (!editor.options.editable) editor.setEditable(true)
    if (editor.view.editable && !hasMetaState && !hasColors) return

    if (snapshot?.binding !== undefined) {
      // Beveiliging een.
      snapshot.binding.unrenderSnapshot()
    } else {
      const { state, view } = editor
      view.dispatch(state.tr.setMeta(ySyncPluginKey, { snapshot: null, prevSnapshot: null }))
    }
  }

  for (const editor of list) restoreOne(editor)

  // Beveiliging drie.
  const begin = Date.now()
  const reconfirm = (): void => {
    if (list.every((editor) => editor.isDestroyed)) return
    for (const editor of list) restoreOne(editor)
    if (Date.now() - begin < RECONFIRMATION_MS) plan(reconfirm)
  }
  plan(reconfirm)
}

/**
 * Het volgende moment om te herbevestigen.
 *
 * `requestAnimationFrame` waar hij bestaat, anders een timer. Niet omdat een test hem mist,
 * maar omdat de renderer ook server-side wordt gedraaid (de pod rekent met dezelfde code) en
 * daar is geen animatieframe.
 */
function plan(work: () => void): void {
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => work())
  else setTimeout(work, 16)
}

/**
 * Welke stand er per document getoond wordt, en welke editors eraan hangen (paneel Geschiedenis).
 *
 * **Een register en geen lus over de editors van nu.** Een dichtgeklapte sectie heeft geen editor, en
 * wie haar tijdens het terugkijken openklapt krijgt een nieuwe. Die moet dezelfde stand tonen als de
 * rest, anders staat er in één sectie ineens de levende, bewerkbare tekst tussen de gekleurde. Daarom
 * meldt elke rich-text-editor zich hier aan ({@link registerEditor}) en neemt hij bij het aanmelden de
 * stand over die voor zijn document geldt. Een WeakMap op het document, zodat een document dat weg is
 * niets vasthoudt.
 */
const EDITORS = new WeakMap<Y.Doc, Set<Editor>>()
const SHOWN = new WeakMap<Y.Doc, { readonly snapshot: Y.Snapshot; readonly prevSnapshot: Y.Snapshot }>()

/** Meldt een editor aan bij zijn document; geeft de afmelding terug. */
export function registerEditor(doc: Y.Doc, editor: Editor): () => void {
  const set = EDITORS.get(doc) ?? new Set<Editor>()
  EDITORS.set(doc, set)
  set.add(editor)
  // Eén tik later, en alleen als de stand dan nog geldt. Een editor die ontstaat in dezelfde render die
  // de stand verlaat (het veld wordt weer bewerkbaar, de lijst met standen verandert, een andere
  // instantie) meldt zich aan vóór het verlaten zijn beslag krijgt; meteen overnemen gaf hem de stand van
  // toen, en een late render daarvan liet die staan zonder markering (valkuil 8).
  setTimeout(() => {
    const shown = SHOWN.get(doc)
    if (shown === undefined || editor.isDestroyed || !set.has(editor)) return
    attributionOf(doc).refreshColors()
    setState([editor], shown.snapshot, shown.prevSnapshot)
  }, 0)
  return () => {
    set.delete(editor)
  }
}

/** De editors die nu aan dit document hangen. */
export function editorsOf(doc: Y.Doc): readonly Editor[] {
  return [...(EDITORS.get(doc) ?? [])]
}

/**
 * Toont in elke editor van dit document een stand uit de geschiedenis: `stand` is "wie schreef wat"
 * op dat moment, `verschil` is "wat is er veranderd sinds dat moment".
 */
export function showHistory(doc: Y.Doc, kind: 'stand' | 'verschil', snapshot: State): void {
  if (kind === 'stand') {
    SHOWN.set(doc, { snapshot, prevSnapshot: Y.emptySnapshot })
    enterHistory(doc, editorsOf(doc), snapshot)
  } else {
    SHOWN.set(doc, { snapshot: Y.snapshot(doc), prevSnapshot: snapshot })
    compareWithNow(doc, editorsOf(doc), snapshot)
  }
}

/** Terug naar de levende stand, met de drie beveiligingen van {@link leaveHistory}. */
export function showLive(doc: Y.Doc): void {
  SHOWN.delete(doc)
  leaveHistory(editorsOf(doc))
}

/** Of dit document nu een stand uit de geschiedenis toont. */
export function showsHistory(doc: Y.Doc): boolean {
  return SHOWN.has(doc)
}
